import React, { 
  useCallback, 
  useEffect, 
  useMemo, 
  useRef, 
  useState 
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Button, 
  Input, 
  message,
  Modal,
  Tabs,
  Tooltip
} from 'antd';
import {
  DownOutlined,
  HomeFilled,
  HomeOutlined,
  RightOutlined,
  StarFilled,
  StarOutlined
} from '@ant-design/icons';
import LevelChip from './LevelChip';
import { selectTree, selectTreeIndex } from '../../../redux/context/selectors';
import { 
  selectCurrentAdmin, 
  selectCurrentWorkspace 
} from '../../../redux/auth/selectors';
import { fetchUser } from '../../../redux/auth/actions';
import { 
  accessShapeOf,
  ancestorsOf, 
  flattenForSwitcher 
} from '../../../redux/context/treeUtils';
import { getRecent } from '../../../redux/context/recent';
import { 
  useCurrentNodeId, 
  useOpenNode 
} from '../../../redux/context/navigation';
import preferencesService from '../../../service/preferences.service';
import type { TreeNode } from '../../../service/tree.service';

const TAB_LABELS: Record<string, string> = {
  recent: 'Recent',
  starred: 'Starred',
  all: 'All'
};

type Scope = 'recent' | 'starred' | 'all';

const hasKids = (node: TreeNode): boolean => (node.children ?? []).length > 0;

interface Row {
  node: TreeNode;
  depth: number;
}

interface ContextSwitcherProps {
  open: boolean;
  onClose: () => void;
}

const ContextSwitcher: React.FC<ContextSwitcherProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const treeFromStore: TreeNode[] | undefined = useSelector(selectTree);
  const roots = useMemo(() => treeFromStore || [], [treeFromStore]);
  const index: any = useSelector(selectTreeIndex);
  const ws: any = useSelector(selectCurrentWorkspace);
  const current: any = useSelector(selectCurrentAdmin);
  const homeId: number | null = current?.home_node_id ?? null;
  const { shape, entityCount, workspaceCount } = useMemo(() => accessShapeOf(roots), [roots]);
  const openNode = useOpenNode();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [scope, setScope] = useState<Scope>('all');
  const [favIds, setFavIds] = useState<number[]>([]);
  const [folded, setFolded] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) {
      setQuery('');
      setScope('all');
      setActive(0);
      preferencesService.getFavourites().then(setFavIds).catch(() => {
        // Starred is a convenience; the other tabs still work.
      });
    }
  }, [open]);

  const searching = query.trim().length > 0;
  const treeRows: Row[] = useMemo(() => {
    const all = flattenForSwitcher(roots);
    if (searching) {
      const q = query.trim().toLowerCase();
      return all.filter((r) => r.node.name.toLowerCase().includes(q));
    }
    // Depth of the nearest folded ancestor; rows deeper than it are hidden.
    let foldedDepth: number | null = null;
    const shown: Row[] = [];
    all.forEach((r) => {
      if (foldedDepth !== null) {
        if (r.depth > foldedDepth) return;
        foldedDepth = null;
      }
      shown.push(r);
      if (hasKids(r.node) && folded.has(r.node.id as number)) foldedDepth = r.depth;
    });
    return shown;
  }, [roots, query, searching, folded]);

  const foldableIds = useMemo(
    () => flattenForSwitcher(roots)
      .filter((r) => hasKids(r.node))
      .map((r) => r.node.id as number),
    [roots]
  );
  const allFolded = foldableIds.length > 0 && foldableIds.every((id) => folded.has(id));

  const searchRef = useRef<any>(null);
  const refocusSearch = () => searchRef.current?.focus();

  const toggleFold = (id: number, fold?: boolean) => {
    setFolded((prev) => {
      const next = new Set(prev);
      const shouldFold = fold ?? !next.has(id);
      if (shouldFold) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const recentRows: Row[] = useMemo(() => {
    if (searching || !open) return [];
    return getRecent()
      .map((id) => index?.[id] as TreeNode | undefined)
      .filter((n): n is TreeNode => !!n)
      .map((node) => ({ node, depth: 0 }));
  }, [searching, open, index]);

  const starredRows: Row[] = useMemo(() => {
    if (searching || !open) return [];
    return favIds
      .map((id) => index?.[id] as TreeNode | undefined)
      .filter((n): n is TreeNode => !!n)
      .map((node) => ({ node, depth: 0 }));
  }, [searching, open, index, favIds]);

  // All is the default tab, then Starred, then Recent.
  const tab: Scope = scope === 'recent' && recentRows.length === 0 ? 'all' : scope;
  const visible = tab === 'recent' ? recentRows : tab === 'starred' ? starredRows : treeRows;
  const tabItems = (['all', 'starred', 'recent'] as Scope[])
    .filter((k) => k !== 'recent' || recentRows.length > 0)
    .map((k) => ({ key: k, label: TAB_LABELS[k] }));

  useEffect(() => {
    setActive(0);
  }, [query]);

  const choose = useCallback(
    (node: TreeNode) => {
      onClose();
      openNode(node);
    },
    [onClose, openNode]
  );

  const toggleStar = async (e: React.MouseEvent, node: TreeNode) => {
    e.stopPropagation();
    const id = node.id as number;
    const starred = favIds.includes(id);
    setFavIds((prev) => (starred ? prev.filter((n) => n !== id) : [...prev, id]));
    try {
      if (starred) await preferencesService.removeFavourite(id);
      else await preferencesService.addFavourite(id);
    } catch {
      setFavIds((prev) => (starred ? [...prev, id] : prev.filter((n) => n !== id)));
      message.error('Could not update your starred list.');
    }
  };

  const makeHome = async (e: React.MouseEvent, node: TreeNode) => {
    e.stopPropagation();
    try {
      await preferencesService.setHome(node.id as number);
      await dispatch<any>(fetchUser());
    } catch {
      message.error('Could not set your home.');
    }
  };

  const soleWorkspace = shape === 'workspaces' && workspaceCount === 1;
  const homeChoice = (node: TreeNode): boolean =>
    (shape === 'entities' && entityCount > 1 && node.kind === 'entity')
    || (shape === 'workspaces' && workspaceCount > 1 && node.kind === 'workspace');

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(visible.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && !searching) {
      const row = visible[active];
      if (row && tab === 'all' && hasKids(row.node)) {
        e.preventDefault();
        toggleFold(row.node.id as number, e.key === 'ArrowLeft');
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = visible[active];
      if (row) choose(row.node);
    }
  };

  const renderRow = (row: Row, pos: number, keyPrefix: string) => {
    const { node, depth } = row;
    const isCurrent = node.kind === 'workspace' && ws?.id === node.id;
    return (
      <div
        key={`${keyPrefix}-${node.id}`}
        onClick={() => choose(node)}
        onMouseEnter={() => setActive(pos)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 8px',
          paddingLeft: 8 + depth * 16,
          cursor: 'pointer',
          borderRadius: 6,
          background: pos === active ? 'rgba(0,0,0,0.06)' : undefined
        }}
      >
        {keyPrefix === 'tree' && !searching && (
          hasKids(node) ? (
            <span
              role="button"
              aria-label={folded.has(node.id as number) ? 'Unfold' : 'Fold'}
              onClick={(e) => {
                e.stopPropagation();
                toggleFold(node.id as number);
                refocusSearch();
              }}
              style={{ width: 14, fontSize: 10, opacity: 0.6 }}
            >
              {folded.has(node.id as number) ? <RightOutlined /> : <DownOutlined />}
            </span>
          ) : (
            <span style={{ width: 14 }} />
          )
        )}
        <LevelChip level={node.kind} compact />
        <span style={{ flex: 1 }}>{node.name}</span>
        {isCurrent && <span 
          style={{ fontSize: 12, opacity: 0.6 }}
        >Current</span>}
        {keyPrefix === 'starred' && node.kind === 'workspace' && node.parent_id != null
          && index?.[node.parent_id] && (
          <span 
            style={{ fontSize: 12, opacity: 0.6 }}
          >in {index[node.parent_id].name}</span>
        )}
        {(node.kind === 'entity' || node.kind === 'workspace') && !soleWorkspace && (
          <Tooltip 
            title={favIds.includes(node.id as number) ? 'Unstar' : 'Star'}
          >
            <Button
              type="text"
              size="small"
              aria-label={favIds.includes(node.id as number) ? 'Unstar' : 'Star'}
              onClick={(e) => toggleStar(e, node)}
              icon={
                favIds.includes(node.id as number) ? (
                  <StarFilled style={{ color: '#f59e0b' }} />
                ) : (
                  <StarOutlined />
                )
              }
            />
          </Tooltip>
        )}
        {homeChoice(node) && (
          <Tooltip 
            title={homeId === node.id ? 'Your home' : 'Make this my home'}
          >
            <Button
              type="text"
              size="small"
              aria-label={homeId === node.id ? 'Your home' : 'Make this my home'}
              onClick={(e) => makeHome(e, node)}
              icon={homeId === node.id ? <HomeFilled /> : <HomeOutlined />}
            />
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <Modal 
      open={open} 
      onCancel={onClose} 
      footer={null} 
      title="Switch context" 
      destroyOnClose
      afterOpenChange={(isOpen) => {
        if (isOpen) {
          refocusSearch();
        }
      }}
    >
      <Input
        autoFocus
        ref={searchRef}
        allowClear
        placeholder="Search organisations, entities and workspaces"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {!searching && (
        <Tabs
          size="small"
          activeKey={tab}
          onChange={(k) => {
            setScope(k as Scope);
            setActive(0);
            refocusSearch();
          }}
          items={tabItems}
          style={{ marginTop: 8 }}
        />
      )}
      {!searching && tab === 'all' && foldableIds.length > 0 && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, marginTop: 8 }}
          onClick={() => {
            setFolded(allFolded ? new Set() : new Set(foldableIds));
            refocusSearch();
          }}
        >
          {allFolded ? 'Expand all' : 'Collapse all'}
        </Button>
      )}
      <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 8 }}>
        {tab === 'recent' && recentRows.map((r, i) => renderRow(r, i, 'recent'))}
        {tab === 'starred' && starredRows.map((r, i) => renderRow(r, i, 'starred'))}
        {tab === 'all' && treeRows.map((r, i) => renderRow(r, i, 'tree'))}
        {visible.length === 0 && <div 
          style={{ padding: 8, opacity: 0.6 }}
        >{tab === 'starred' && !searching
            ? 'Star entities or workspaces to keep them here.'
            : 'No matches'}</div>}
      </div>
    </Modal>
  );
};

export const useSwitcherHotkeys = (setOpen: (open: boolean) => void) => {
  const index: any = useSelector(selectTreeIndex);
  const currentId = useCurrentNodeId();
  const openNode = useOpenNode();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        !!t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable);
      const isK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      const isSlash = e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (isK || isSlash) {
        e.preventDefault();
        setOpen(true);
      } else if (e.altKey && e.key === 'ArrowUp' && !typing) {
        const id = currentId;
        if (id == null || !index?.[id]) return;
        const parent = ancestorsOf(index, id)
          .filter((n) => n.id !== id)
          .pop();
        if (parent) {
          e.preventDefault();
          openNode(parent);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setOpen, index, currentId, openNode]);
};

export default ContextSwitcher;
